import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  createFitnessFunction,
  deleteFitnessFunction,
  listFitnessFunctions,
  updateFitnessFunction,
  type FitnessFunction,
  type FitnessFunctionDefinition,
} from '../api/fitnessFunctionsApi';

interface Props {
  projectId: string;
}

type TemplateKind = 'relation-protocol-required' | 'shared-resource';

interface FitnessFunctionRow extends FitnessFunction {
  source?: 'builtin' | 'custom' | string;
  template_key?: string;
}

interface FormState {
  kind: TemplateKind;
  name: string;
  templateKey: string;
  enabled: boolean;
  message: string;
  level: string;
  requiredText: string;
  fromKinds: string;
  toKinds: string;
  consumerKinds: string;
  resourceKinds: string;
  minConsumers: string;
}

const emptyForm: FormState = {
  kind: 'relation-protocol-required',
  name: '',
  templateKey: '',
  enabled: true,
  message: '',
  level: 'container',
  requiredText: '',
  fromKinds: '',
  toKinds: '',
  consumerKinds: '',
  resourceKinds: '',
  minConsumers: '2',
};

const splitList = (value: string): string[] =>
  value.split(',').map((item) => item.trim()).filter(Boolean);

const getKind = (definition?: FitnessFunctionDefinition): string =>
  typeof definition?.kind === 'string' ? definition.kind : '—';

const buildDefinition = (form: FormState): FitnessFunctionDefinition => {
  const common = {
    kind: form.kind,
    level: form.level.trim(),
    ...(form.message.trim() ? { message: form.message.trim() } : {}),
  };

  if (form.kind === 'shared-resource') {
    return {
      ...common,
      consumer_kinds: splitList(form.consumerKinds),
      resource_kinds: splitList(form.resourceKinds),
      min_consumers: Number(form.minConsumers),
    };
  }

  return {
    ...common,
    required_text: form.requiredText.trim(),
    from_kinds: splitList(form.fromKinds),
    to_kinds: splitList(form.toKinds),
  };
};

const FitnessFunctionsSection: React.FC<Props> = ({ projectId }) => {
  const [functions, setFunctions] = useState<FitnessFunctionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFunctions(await listFitnessFunctions(projectId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleEnabled = async (fn: FitnessFunctionRow) => {
    const nextEnabled = !Boolean(fn.enabled);
    setUpdatingId(fn.id);
    setError(null);
    try {
      const payload = fn.source === 'builtin'
        ? { enabled: nextEnabled }
        : {
            name: fn.name,
            description: fn.description,
            template_key: fn.template_key,
            enabled: nextEnabled,
            definition: fn.definition,
          };
      await updateFitnessFunction(projectId, fn.id, payload);
      setSuccessMsg(`Обновлено: ${fn.name}`);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteCustom = async (fn: FitnessFunctionRow) => {
    if (fn.source === 'builtin') return;
    if (!window.confirm(`Удалить fitness function "${fn.name}"?`)) return;
    setError(null);
    try {
      await deleteFitnessFunction(projectId, fn.id);
      setSuccessMsg(`Удалено: ${fn.name}`);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const createCustom = async () => {
    setFormError(null);
    setSaving(true);
    try {
      await createFitnessFunction(projectId, {
        name: form.name.trim(),
        template_key: form.templateKey.trim(),
        enabled: form.enabled,
        definition: buildDefinition(form),
      });
      setDialogOpen(false);
      setSuccessMsg(`Создано: ${form.name.trim()}`);
      await load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ marginBottom: 32 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>Fitness Functions</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openCreate}>
          Добавить
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Источник</TableCell>
              <TableCell>Активно</TableCell>
              <TableCell>Ключ шаблона</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {functions.map((fn) => {
              const isBuiltin = fn.source === 'builtin';
              return (
                <TableRow key={fn.id} hover>
                  <TableCell>{fn.name}</TableCell>
                  <TableCell>
                    <Chip label={fn.source ?? 'custom'} size="small" color={isBuiltin ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={Boolean(fn.enabled)}
                      disabled={updatingId === fn.id}
                      onChange={() => toggleEnabled(fn)}
                      inputProps={{ 'aria-label': `${fn.name} enabled` }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, opacity: 0.8 }}>{fn.template_key ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{getKind(fn.definition)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isBuiltin}
                        aria-label={`Удалить ${fn.name}`}
                        title={isBuiltin ? 'Встроенные функции нельзя удалить' : 'Удалить'}
                        onClick={() => deleteCustom(fn)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {functions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" variant="body2">Нет fitness functions</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Создать fitness function</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="fitness-template-kind-label">Тип шаблона</InputLabel>
            <Select
              labelId="fitness-template-kind-label"
              label="Тип шаблона"
              value={form.kind}
              onChange={(e) => setField('kind', e.target.value as TemplateKind)}
            >
              <MenuItem value="relation-protocol-required">relation-protocol-required</MenuItem>
              <MenuItem value="shared-resource">shared-resource</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Название" fullWidth margin="normal" required value={form.name} onChange={(e) => setField('name', e.target.value)} />
          <TextField label="Ключ шаблона" fullWidth margin="normal" required value={form.templateKey} onChange={(e) => setField('templateKey', e.target.value)} />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={form.enabled} onChange={(e) => setField('enabled', e.target.checked)} />}
            label="Активно"
          />
          <TextField label="Сообщение" fullWidth margin="normal" value={form.message} onChange={(e) => setField('message', e.target.value)} />
          <TextField label="Уровень" fullWidth margin="normal" value={form.level} onChange={(e) => setField('level', e.target.value)} />
          {form.kind === 'shared-resource' ? (
            <>
              <TextField label="Типы потребителей" fullWidth margin="normal" value={form.consumerKinds} onChange={(e) => setField('consumerKinds', e.target.value)} helperText="Через запятую" />
              <TextField label="Типы ресурсов" fullWidth margin="normal" value={form.resourceKinds} onChange={(e) => setField('resourceKinds', e.target.value)} helperText="Через запятую" />
              <TextField label="Минимум потребителей" fullWidth margin="normal" type="number" value={form.minConsumers} onChange={(e) => setField('minConsumers', e.target.value)} />
            </>
          ) : (
            <>
              <TextField label="Обязательный текст" fullWidth margin="normal" value={form.requiredText} onChange={(e) => setField('requiredText', e.target.value)} />
              <TextField label="Типы источников" fullWidth margin="normal" value={form.fromKinds} onChange={(e) => setField('fromKinds', e.target.value)} helperText="Через запятую" />
              <TextField label="Типы назначений" fullWidth margin="normal" value={form.toKinds} onChange={(e) => setField('toKinds', e.target.value)} helperText="Через запятую" />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={createCustom} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default FitnessFunctionsSection;
